import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAdminIssuesFilterOptions,
  fetchAdminIssuesStoreOptions,
  type AdminIssuesFilterOptions,
  type AdminIssuesStoreOptions,
} from '../api/adminIssuesApi';

export const adminIssuesFilterOptionsQueryKey = (serviceDeskId: number) => [
  'adminIssuesFilterOptions',
  serviceDeskId,
];

export const adminIssuesStoreOptionsQueryKey = (serviceDeskId: number) => [
  'adminIssuesStoreOptions',
  serviceDeskId,
];

// Stable identity: a fresh object here would rebuild the merged options on every render.
const NO_STORES: AdminIssuesStoreOptions = { stores: [], sectors: [] };

/**
 * Two requests behind one shape. Statuses and request types answer in about a second; the
 * stores and sectors sit behind a desk-wide Assets scan that takes several. Splitting them
 * lets the status and request-type dropdowns fill immediately — the store and sector filters
 * hide themselves while their lists are still empty, and appear when they land.
 */
export const useAdminIssuesFilterOptions = (serviceDeskId: number) => {
  const statusesAndTypes = useQuery({
    queryKey: adminIssuesFilterOptionsQueryKey(serviceDeskId),
    queryFn: () => fetchAdminIssuesFilterOptions(serviceDeskId),
    enabled: !!serviceDeskId,
    staleTime: 5 * 60 * 1000,
  });

  const stores = useQuery({
    queryKey: adminIssuesStoreOptionsQueryKey(serviceDeskId),
    queryFn: () => fetchAdminIssuesStoreOptions(serviceDeskId),
    enabled: !!serviceDeskId,
    staleTime: 5 * 60 * 1000,
  });

  const data = useMemo(
    (): AdminIssuesFilterOptions | undefined =>
      statusesAndTypes.data && { ...statusesAndTypes.data, ...(stores.data ?? NO_STORES) },
    [statusesAndTypes.data, stores.data]
  );

  return { ...statusesAndTypes, data };
};
